import { DocumentTextIcon, PaperClipIcon } from "@heroicons/react/24/outline";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { ApiResponse } from "@/api/rest/core/types";
import {
  DefaultWarrantyCategory,
  SubmitWarrantyArgs,
  WarrantyDraftFile,
  WarrantySubcontractor,
} from "@/api/rest/services/warrantyService";
import UploadFilesPopup from "@/components/UploadFilesPopup";
import { Banner } from "@/components/common/Banner";
import Button from "@/components/common/Button";
import UploadStatus from "@/components/common/UploadStatus";

interface WarrantySubmissionFormProps {
  initialData: SubmitWarrantyArgs;
  subcontractors: WarrantySubcontractor[];
  defaultCategory?: DefaultWarrantyCategory | null;
  selectedCategoryId?: string | null;
  onSubmit: (data: SubmitWarrantyArgs) => void;
  isSubmitting: boolean;
  children?: React.ReactNode;
  pendingFiles: File[];
  uploadedDraftFiles: WarrantyDraftFile[];
  submitFiles: (files: File[], onProgress: (fraction: number) => void) => Promise<ApiResponse<unknown>>;
  uploadProgress: number | null;
  submitError: string | null;
}

const WarrantySubmissionForm: React.FC<WarrantySubmissionFormProps> = ({
  initialData,
  subcontractors,
  defaultCategory,
  selectedCategoryId,
  onSubmit,
  isSubmitting,
  children,
  pendingFiles,
  uploadedDraftFiles,
  submitFiles,
  uploadProgress,
  submitError,
}) => {
  const { t } = useTranslation();

  const descriptionRef = useRef<HTMLTextAreaElement>(null);

  // Build flat list of all subcontractors for dropdown with categories, including default category
  // Use index as unique identifier since emails can be shared across subcontractors
  const subcontractorOptions = useMemo(() => {
    const options = subcontractors.map((sub, index) => {
      const categoryNames = sub.categories.map((cat) => cat.name).join(", ");
      const categoryIds = sub.categories.map((cat) => cat.id);
      return {
        id: `sub-${index}`,
        email: sub.email,
        name: sub.name,
        displayName: categoryNames ? `${sub.name} (${categoryNames})` : sub.name,
        categoryIds,
      };
    });

    // Add default category (Developer) if configured
    // When no email is set, emails are sent to all building admins
    if (defaultCategory) {
      const defaultName = defaultCategory.name || t("warranty.developer");
      options.push({
        id: "default",
        email: defaultCategory.email || "",
        name: defaultName,
        displayName: defaultName,
        categoryIds: [] as string[],
      });
    }

    return options;
  }, [subcontractors, defaultCategory, t]);

  // Find the subcontractor option that handles the selected category
  const initialOptionId = useMemo(() => {
    if (selectedCategoryId) {
      const matchingOption = subcontractorOptions.find((opt) => opt.categoryIds.includes(selectedCategoryId));
      if (matchingOption) {
        return matchingOption.id;
      }
    }
    // Fallback: find by email from initialData
    const fallbackOption = subcontractorOptions.find((opt) => opt.email === initialData.subcontractorEmail);
    return fallbackOption?.id ?? subcontractorOptions[0]?.id ?? "";
  }, [selectedCategoryId, subcontractorOptions, initialData.subcontractorEmail]);

  const [selectedOptionId, setSelectedOptionId] = useState(initialOptionId);

  // Update selection when the AI selects a category
  useEffect(() => {
    setSelectedOptionId(initialOptionId);
  }, [initialOptionId]);

  // Track if user has manually changed the subcontractor selection
  const [userChangedSubcontractor, setUserChangedSubcontractor] = useState(false);

  // Get the data for the currently selected option
  const selectedSubcontractorData = useMemo(() => {
    const option = subcontractorOptions.find((opt) => opt.id === selectedOptionId);
    if (!option) {
      return {
        email: initialData.subcontractorEmail,
        name: null as string | null,
      };
    }

    return {
      email: option.email,
      name: option.name,
    };
  }, [selectedOptionId, subcontractorOptions, initialData.subcontractorEmail]);

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const description = descriptionRef.current?.value.trim() ?? "";
    if (!description) {
      return;
    }

    onSubmit({
      ...initialData,
      subcontractorEmail: selectedSubcontractorData.email,
      subcontractorName: selectedSubcontractorData.name,
      // Only include category if user hasn't manually changed the subcontractor
      // If they changed it, set to null to indicate manual override
      categoryId: userChangedSubcontractor ? null : undefined,
      categoryName: userChangedSubcontractor ? null : undefined,
      description,
    });
  };

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-4">
      {initialData.likelyUnderWarranty ? (
        <Banner success message={t("warranty.likelyCovered")} />
      ) : (
        <Banner success={false} message={t("warranty.mayNotBeCovered")} />
      )}

      {subcontractorOptions.length > 0 && (
        <div className="flex flex-col gap-1">
          <label className="text-sm font-medium text-gray-700">{t("warranty.subcontractor")}</label>
          <select
            className="border border-gray-300 rounded-md p-2 w-full"
            value={selectedOptionId}
            onChange={(e) => {
              setSelectedOptionId(e.target.value);
              setUserChangedSubcontractor(true);
            }}
          >
            {subcontractorOptions.map((option) => (
              <option key={option.id} value={option.id}>
                {option.displayName}
              </option>
            ))}
          </select>
        </div>
      )}

      <div className="flex flex-col gap-1">
        <label className="text-sm font-medium text-gray-700">{t("warranty.description")}</label>
        <textarea
          className="border border-gray-300 rounded-md p-2 w-full"
          name="description"
          rows={6}
          placeholder={t("warranty.caseDescription")}
          defaultValue={initialData.description}
          ref={descriptionRef}
          required
          onKeyDown={(e) => {
            if ((e.metaKey || e.ctrlKey) && e.key === "Enter") {
              e.preventDefault();
              e.currentTarget.form?.requestSubmit();
            }
          }}
        />
      </div>

      {children}

      <UploadFilesPopup className="flex flex-row" uploadCallback={submitFiles}>
        <Button
          type="button"
          className="rounded-md py-2 px-4 flex-grow !bg-orange-200 hover:!bg-orange-300 !text-orange-950"
          onClick={(event) => event.preventDefault()}
        >
          <PaperClipIcon className="w-4 h-4 inline -mt-1 ml-4 mr-1" />
          {t("warranty.addFiles")}
          <span className="w-4 h-4 inline ml-1 mr-4" />
        </Button>
      </UploadFilesPopup>

      {(pendingFiles.length > 0 || uploadedDraftFiles.length > 0) && (
        <div className="flex flex-row flex-wrap gap-2">
          {uploadedDraftFiles.map((file) => (
            <span key={`draft-${file.entryId}`} className="rounded-md py-2 px-4 bg-gray-200 flex items-center">
              <DocumentTextIcon className="w-4 h-4 inline -mt-0.5 mr-2" />
              {file.filename ?? t("warranty.file")}
            </span>
          ))}
          {pendingFiles.map((file, i) => (
            <span key={i} className="rounded-md py-2 px-4 bg-gray-200 flex items-center">
              <DocumentTextIcon className="w-4 h-4 inline -mt-0.5 mr-2" />
              {file.name}
            </span>
          ))}
        </div>
      )}

      <UploadStatus progress={uploadProgress} error={submitError} />

      <Button type="submit" variant="primary" loading={isSubmitting} disabled={isSubmitting}>
        {t("warranty.submitClaim")}
      </Button>
    </form>
  );
};

export default WarrantySubmissionForm;
